import React from 'react';
import { Spin } from 'antd';

export const PageFallback = () => (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 240 }}>
        <Spin size="large" />
    </div>
);

/** Lazy import with one automatic reload when a webpack chunk hash is stale. */
export function lazyWithRetry(factory) {
    return React.lazy(() =>
        factory().catch((error) => {
            const isChunkError =
                error?.name === 'ChunkLoadError'
                || /Loading chunk [\w/-]+ failed/i.test(String(error?.message || error));
            if (isChunkError) {
                const key = 'upos_lazy_chunk_reload';
                if (!sessionStorage.getItem(key)) {
                    sessionStorage.setItem(key, '1');
                    window.location.reload();
                    return new Promise(() => {});
                }
                sessionStorage.removeItem(key);
            }
            throw error;
        })
    );
}

export const DashboardPage = React.lazy(() => import('../pages/DashboardPage'));
export const ProductsPage = React.lazy(() => import('../pages/ProductsPage'));
export const CustomersPage = React.lazy(() => import('../pages/CustomersPage'));
export const CustomerFormPage = React.lazy(() => import('../pages/customers/CustomerFormPage'));
export const CustomerDetailPage = React.lazy(() => import('../pages/customers/CustomerDetailPage'));
export const CustomerPaymentFormPage = React.lazy(() => import('../pages/customers/CustomerPaymentFormPage'));
export const ProductFormPage = React.lazy(() => import('../pages/products/ProductFormPage'));
export const ProductCategoriesPage = React.lazy(() => import('../pages/products/ProductCategoriesPage'));
export const UserFormPage = React.lazy(() => import('../pages/users/UserFormPage'));
export const SalesPage = React.lazy(() => import('../pages/SalesPage'));
export const ReportsPage = React.lazy(() => import('../pages/ReportsPage'));
export const SettingsPage = React.lazy(() => import('../pages/SettingsPage'));
export const UsersPage = React.lazy(() => import('../pages/UsersPage'));
export const SuperAdminPage = React.lazy(() => import('../pages/SuperAdminPage'));
export const ProfilePage = React.lazy(() => import('../pages/ProfilePage'));
export const BankAccountsPage = React.lazy(() => import('../pages/bank/BankAccountsPage'));
export const BankAccountFormPage = React.lazy(() => import('../pages/bank/BankAccountFormPage'));
export const BankPaymentsPage = React.lazy(() => import('../pages/bank/BankPaymentsPage'));
export const BankPaymentFormPage = React.lazy(() => import('../pages/bank/BankPaymentFormPage'));
export const BankReceiptsPage = React.lazy(() => import('../pages/bank/BankReceiptsPage'));
export const BankReceiptFormPage = React.lazy(() => import('../pages/bank/BankReceiptFormPage'));
export const BankTransfersPage = React.lazy(() => import('../pages/bank/BankTransfersPage'));
export const BankTransferFormPage = React.lazy(() => import('../pages/bank/BankTransferFormPage'));
export const SaleDetailPage = React.lazy(() => import('../pages/sales/SaleDetailPage'));
export const SalesInvoicesPage = React.lazy(() => import('../pages/sales/SalesInvoicesPage'));
export const SalesInvoiceFormPage = React.lazy(() => import('../pages/sales/SalesInvoiceFormPage'));
export const SalesReturnsPage = React.lazy(() => import('../pages/sales/SalesReturnsPage'));
export const SalesReturnFormPage = React.lazy(() => import('../pages/sales/SalesReturnFormPage'));
export const SalesReceiptsPage = React.lazy(() => import('../pages/sales/SalesReceiptsPage'));
export const SalesReceiptFormPage = React.lazy(() => import('../pages/sales/SalesReceiptFormPage'));
export const SalesReceiptDetailPage = React.lazy(() => import('../pages/sales/SalesReceiptDetailPage'));
export const TenantFormPage = React.lazy(() => import('../pages/TenantFormPage'));
export const SuppliersPage = React.lazy(() => import('../pages/purchases/SuppliersPage'));
export const SupplierFormPage = React.lazy(() => import('../pages/purchases/SupplierFormPage'));
export const SupplierDetailPage = React.lazy(() => import('../pages/purchases/SupplierDetailPage'));
export const PurchaseBillsPage = React.lazy(() => import('../pages/purchases/PurchaseBillsPage'));
export const PurchaseBillFormPage = React.lazy(() => import('../pages/purchases/PurchaseBillFormPage'));
export const PurchasePaymentsPage = React.lazy(() => import('../pages/purchases/PurchasePaymentsPage'));
export const PurchasePaymentFormPage = React.lazy(() => import('../pages/purchases/PurchasePaymentFormPage'));
export const InventoryLocationsPage = React.lazy(() => import('../pages/inventory/InventoryLocationsPage'));
export const InventoryLocationFormPage = React.lazy(() => import('../pages/inventory/InventoryLocationFormPage'));
export const StockAdjustmentsPage = React.lazy(() => import('../pages/inventory/StockAdjustmentsPage'));
export const StockAdjustmentFormPage = React.lazy(() => import('../pages/inventory/StockAdjustmentFormPage'));
export const StockTransfersPage = React.lazy(() => import('../pages/inventory/StockTransfersPage'));
export const StockTransferFormPage = React.lazy(() => import('../pages/inventory/StockTransferFormPage'));
export const InventoryBatchesPage = React.lazy(() => import('../pages/inventory/InventoryBatchesPage'));
export const InventoryBatchFormPage = React.lazy(() => import('../pages/inventory/InventoryBatchFormPage'));
export const LowStockPage = React.lazy(() => import('../pages/inventory/LowStockPage'));
export const InventoryUnitsPage = React.lazy(() => import('../pages/inventory/InventoryUnitsPage'));
export const InventoryUnitFormPage = React.lazy(() => import('../pages/inventory/InventoryUnitFormPage'));
export const InventoryKitFormPage = React.lazy(() => import('../pages/inventory/InventoryKitFormPage'));
export const ChartOfAccountsPage = React.lazy(() => import('../pages/accounting/ChartOfAccountsPage'));
export const ChartAccountFormPage = React.lazy(() => import('../pages/accounting/ChartAccountFormPage'));
export const ChartOpeningBalancePage = React.lazy(() => import('../pages/accounting/ChartOpeningBalancePage'));
export const JournalsPage = React.lazy(() => import('../pages/accounting/JournalsPage'));
export const JournalFormPage = React.lazy(() => import('../pages/accounting/JournalFormPage'));
export const JournalDetailPage = React.lazy(() => import('../pages/accounting/JournalDetailPage'));
export const FinancialReportsPage = React.lazy(() => import('../pages/accounting/FinancialReportsPage'));
export const FiscalYearPage = React.lazy(() => import('../pages/accounting/FiscalYearPage'));
export const FiscalYearFormPage = React.lazy(() => import('../pages/accounting/FiscalYearFormPage'));
export const TaxRatesPage = React.lazy(() => import('../pages/tax/TaxRatesPage'));
export const TaxRateFormPage = React.lazy(() => import('../pages/tax/TaxRateFormPage'));
export const FbrPage = React.lazy(() => import('../pages/tax/FbrPage'));
export const PrintTemplatesPage = React.lazy(() => import('../pages/printing/PrintTemplatesPage'));
export const PrintTemplateFormPage = React.lazy(() => import('../pages/printing/PrintTemplateFormPage'));
export const EnterpriseAuditPage = React.lazy(() => import('../pages/enterprise/EnterpriseAuditPage'));
export const CompanyAccessPage = React.lazy(() => import('../pages/enterprise/CompanyAccessPage'));
export const CompanyAccessGrantPage = React.lazy(() => import('../pages/enterprise/CompanyAccessGrantPage'));
export const AccessScopesPage = React.lazy(() => import('../pages/enterprise/AccessScopesPage'));
export const ScheduledTasksPage = React.lazy(() => import('../pages/enterprise/ScheduledTasksPage'));
export const ScheduledTaskFormPage = React.lazy(() => import('../pages/enterprise/ScheduledTaskFormPage'));
export const SalesQuotationsPage = React.lazy(() => import('../pages/sales/SalesQuotationsPage'));
export const SalesQuotationFormPage = React.lazy(() => import('../pages/sales/SalesQuotationFormPage'));
export const ExpenseIncomePage = React.lazy(() => import('../pages/accounting/ExpenseIncomePage'));
export const PurchaseReturnsPage = React.lazy(() => import('../pages/purchases/PurchaseReturnsPage'));
export const PurchaseReturnFormPage = React.lazy(() => import('../pages/purchases/PurchaseReturnFormPage'));
export const PurchaseReturnDetailPage = React.lazy(() => import('../pages/purchases/PurchaseReturnDetailPage'));
